import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

const STORAGE_KEY = 'jpro_screensaver_settings';

const DEFAULTS = {
  timeoutMs: 180000,        // 3 minutes
  theme: 'particles',       // particles | rain | aurora | starfield | blobs | matrix | compliance | blueprint | fireflies | bokeh | synthwave | waves | minimal
  clockStyle: 'digital',    // digital | analog | roman | minimal | flip | word | segment
  showWeather: true,
  showDate: true,
  showSeconds: true,
  background: 'auto',       // auto | solid_dark | solid_light | gradient_sunset | gradient_aurora | gradient_ocean | gradient_dawn | gradient_compliance
  colorMode: 'auto',        // auto | vibrant | cycling
  accentColor: '#3b82f6',
};

const PALETTES = {
  auto: ['#3b82f6', '#60a5fa', '#8b5cf6'],
  vibrant: ['#f43f5e', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6'],
  cycling: ['#f43f5e', '#fb923c', '#facc15', '#34d399', '#22d3ee', '#60a5fa', '#a78bfa', '#ec4899'],
};

const BACKGROUND_STYLES = {
  auto: null, // handled by consumer based on app theme
  solid_dark: { background: '#030712' },
  solid_light: { background: '#f9fafb' },
  gradient_sunset: { background: 'linear-gradient(135deg, #1e1b4b 0%, #7c2d12 50%, #ea580c 100%)' },
  gradient_aurora: { background: 'linear-gradient(135deg, #064e3b 0%, #0f766e 40%, #1e3a8a 100%)' },
  gradient_ocean: { background: 'linear-gradient(180deg, #0c4a6e 0%, #0f172a 100%)' },
  gradient_dawn: { background: 'linear-gradient(180deg, #fef3c7 0%, #fbcfe8 50%, #c7d2fe 100%)' },
  gradient_compliance: { background: 'linear-gradient(135deg, #0f172a 0%, #1e3a8a 60%, #0369a1 100%)' },
};

const ScreensaverContext = createContext(null);

function loadSettings() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULTS };
    const parsed = JSON.parse(raw);
    return { ...DEFAULTS, ...parsed };
  } catch {
    return { ...DEFAULTS };
  }
}

export function ScreensaverProvider({ children }) {
  const [settings, setSettings] = useState(loadSettings);
  const [manualActive, setManualActive] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch {}
  }, [settings]);

  const updateSetting = useCallback((key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  }, []);

  const updateSettings = useCallback((partial) => {
    setSettings(prev => ({ ...prev, ...partial }));
  }, []);

  const resetSettings = useCallback(() => {
    setSettings({ ...DEFAULTS });
  }, []);

  const manualTrigger = useCallback(() => {
    setManualActive(true);
  }, []);

  const manualDismiss = useCallback(() => {
    setManualActive(false);
  }, []);

  // Global keyboard shortcut: Ctrl+Shift+S (or Cmd+Shift+S on mac)
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'S' && e.shiftKey && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        setManualActive(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const palette = useMemo(() => {
    const base = PALETTES[settings.colorMode] || PALETTES.auto;
    if (settings.colorMode === 'auto' && settings.accentColor) {
      return [settings.accentColor, ...base];
    }
    return base;
  }, [settings.colorMode, settings.accentColor]);

  const value = useMemo(() => ({
    settings,
    updateSetting,
    updateSettings,
    resetSettings,
    manualActive,
    manualTrigger,
    manualDismiss,
    palette,
    backgroundStyles: BACKGROUND_STYLES,
  }), [settings, updateSetting, updateSettings, resetSettings, manualActive, manualTrigger, manualDismiss, palette]);

  return (
    <ScreensaverContext.Provider value={value}>
      {children}
    </ScreensaverContext.Provider>
  );
}

export function useScreensaver() {
  const ctx = useContext(ScreensaverContext);
  if (!ctx) throw new Error('useScreensaver must be used within ScreensaverProvider');
  return ctx;
}

export const SCREENSAVER_DEFAULTS = DEFAULTS;
export const SCREENSAVER_PALETTES = PALETTES;
export const SCREENSAVER_BACKGROUNDS = BACKGROUND_STYLES;
